// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import cx from 'classnames';

import { ImportInformationType, ExportInformationType, ReportInformationType } from '@shared/constants/data-management';
import CloseIcon from '@shared/icons/Close';
import Flex from '@shared/components/Flex';
import themev5 from '@core/themev5';
import { alpha } from '@mui/material';

type DataManagementInformationType = ImportInformationType | ExportInformationType | ReportInformationType;

// Prop interface for the main class.
export interface InformationTypeProps {
  className?: string;
  types: Array<{ type: DataManagementInformationType; text: string }>;
  currentType?: DataManagementInformationType;
  onSelect: (type: DataManagementInformationType) => any;
  onClose?: () => any;
}

// Class to show the type selection panel which
// consists of list of import/export/report types (Assets, Platforms, ...) and 
// in the case of 'import' a 'Close' button (x) if a type is selected.
class InformationType extends React.Component<InformationTypeProps> {

  // Invoke cb to open a panel for user to browse for an import file.
  private handleTypeClick = (type: DataManagementInformationType) => (e) => {
    const { onSelect } = this.props;
    onSelect(type);
  }

  // Invoke cb to close the panel to browse for files.
  private handleClose = (e) => {
    e.stopPropagation();

    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }
  }

  render() {
    const { types, currentType } = this.props;
    const classes = withStyles.getClasses(this.props);

    return (
      <div className={classes.root}>
        {types.map(({ type, text }) => { // List of types.
          const isCurrentType = currentType === type;

          return (
            <Flex
              key={type}
              alignItems="center"
              justifyContent="space-between"
              className={
                cx(classes.informationType,
                  {
                    [classes.informationTypeActive]: isCurrentType // concatenate 'informationTypeActive' style if 'isCurrentType' is true.
                  }
                )
              }
              onClick={this.handleTypeClick(type)}
            >
              <span // label for types
                className={classes.informationTypeText}>{text}
              </span> 

              {isCurrentType && (
                <CloseIcon // Show an (x) to close the browsing panel if a type is selected.
                  className={classes.closeinformationTypeIcon}
                  onClick={this.handleClose}
                />
              )}
            </Flex>
          );
        })}
      </div>
    );
  }
}

const activeInformationTypeStyles = {
  color: themev5.palette.colors.primaryAccent,
  backgroundColor: alpha(themev5.palette.colors.primaryAccent, .04),
  borderColor: 'transparent',

  '& $informationTypeText': {
    borderColor: 'transparent',
  }
};

const InformationTypeStyled = withStyles(
  InformationType, 
  (theme, props) => ({
    "root": {},
    "informationType": {
      cursor: 'pointer',
      height: 40,
      padding: theme.spacing(0, 6, 0, 4),
      '&:hover': activeInformationTypeStyles,
    },
    "informationTypeActive": activeInformationTypeStyles,
    "informationTypeText": {
      ...theme.shortcuts.borderPrimaryDashed,
    },
    "closeinformationTypeIcon": {
      fontSize: 14,
      '& path': {
        fill: theme.palette.colors.grey,
      },
    },
  })
);

export default InformationTypeStyled;